import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {AI_AGENT_GO_TO_PAGE_DEFAULT_KEY, AI_AGENT_GO_TO_PAGE_KEYS} from '../../constants';
import type {AiAgentExecuteResult} from '../../../../redux/aiAgent/aiAgent.type';
import * as css from './AiAgentGoToPage.css';

interface AiAgentGoToPageProps {
    record: AiAgentExecuteResult;
    disabled?: boolean;
    onOpen: () => void | Promise<void>;
}

/** The aiAgent i18n key naming what the button opens — an intent it does not know gets the generic one. */
export function getGoToPageKey(intent: string): string {
    return Object.hasOwn(AI_AGENT_GO_TO_PAGE_KEYS, intent)
        ? AI_AGENT_GO_TO_PAGE_KEYS[intent]
        : AI_AGENT_GO_TO_PAGE_DEFAULT_KEY;
}

/**
 * Shown under the chat once the record it agreed on exists: the way to that record. The app
 * opened it on its own right after creation, but that opening can fail, and on a phone the
 * panel covers the record — this button brings the producer to it whenever they want.
 */
function AiAgentGoToPage({record, disabled = false, onOpen}: AiAgentGoToPageProps) {
    const {t} = useTranslation('aiAgent');

    const handleOpen = useCallback(() => {
        if(disabled) {
            return;
        }
        void onOpen();
    }, [disabled, onOpen]);

    return (
        <css.goToPageButton
            type="button"
            disabled={disabled}
            onClick={handleOpen}
            data-cy="ai-agent-go-to-page"
        >
            {t(getGoToPageKey(record.intent))}
            <css.arrow aria-hidden="true">›</css.arrow>
        </css.goToPageButton>
    );
}

export default AiAgentGoToPage;
